import { useEffect, useMemo, useRef, useState } from 'react'
import type { EnrichedPublicShop } from '../../../stores/shopStore'
import { computeMallLayout } from './mallLayout'
import { MallSceneManager } from './mallScene'
import { useMallInputController } from './useMallInputController'
import FloorSelectModal from './FloorSelectModal'
import { AMBIENCE, loadAmbience, saveAmbience, type AmbienceId } from './mallAmbience'
import { loadControlMode, saveControlMode, type ControlMode } from './mallControlMode'
import MobileJoystick from './MobileJoystick'
import ScanPanel from '../ScanPanel'
import './Mall3DViewer.css'

interface Props {
  shops: EnrichedPublicShop[]
  onExit: () => void
  onShopClick: (shop: EnrichedPublicShop) => void
  onShare: (e: React.MouseEvent, shop: EnrichedPublicShop) => void
}

export default function Mall3DViewer({ shops, onExit, onShopClick, onShare }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<MallSceneManager | null>(null)

  const layout = useMemo(() => computeMallLayout(shops), [shops])

  const [currentFloorId, setCurrentFloorId] = useState<string | null>(null)
  const [floorModalOpen, setFloorModalOpen] = useState(false)
  // Tracks whichever shop the scene currently reports as in-proximity, regardless
  // of whether its card is showing — separate from `dismissedShopId` below so a
  // panel closed by mistake can be brought back without having to walk away and
  // back into range.
  const [nearbyShop, setNearbyShop] = useState<EnrichedPublicShop | null>(null)
  const [dismissedShopId, setDismissedShopId] = useState<string | null>(null)
  const [hintVisible, setHintVisible] = useState(true)
  const [ambience, setAmbience] = useState<AmbienceId>(loadAmbience)
  const [controlMode, setControlMode] = useState<ControlMode>(loadControlMode)

  const activeShop = nearbyShop && nearbyShop.id !== dismissedShopId ? nearbyShop : null

  // A fresh closure each render is fine here — useMallInputController re-reads
  // this through a ref on every render, so there's no stale-closure risk from
  // skipping useCallback.
  const handleInteract = () => {
    if (activeShop) {
      onShopClick(activeShop)
    } else if (nearbyShop) {
      // Card was dismissed while still standing in front of the shop —
      // bring it back rather than walking in blind.
      setDismissedShopId(null)
    }
  }

  const { pointerLocked, releasePointer } = useMallInputController(containerRef, sceneRef, {
    onInteract: handleInteract,
    controlMode,
  })

  const toggleControlMode = () => {
    const next: ControlMode = controlMode === 'joystick' ? 'gesture' : 'joystick'
    setControlMode(next)
    saveControlMode(next)
  }

  // The scene reads the mood once at construction; later changes go through
  // setAmbience() so switching never rebuilds the mall.
  const ambienceRef = useRef(ambience)
  ambienceRef.current = ambience

  const toggleAmbience = () => {
    const next: AmbienceId = ambience === 'warm' ? 'daylight' : 'warm'
    setAmbience(next)
    saveAmbience(next)
    sceneRef.current?.setAmbience(next)
  }

  // Mount/dispose the three.js scene
  useEffect(() => {
    const container = containerRef.current
    if (!container || layout.floors.length === 0) return

    const manager = new MallSceneManager(container, layout, {
      onProximityChange: setNearbyShop,
      ambience: ambienceRef.current,
    })
    sceneRef.current = manager
    setCurrentFloorId(manager.getCurrentFloorId())

    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      manager.resize(width, height)
    })
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      manager.dispose()
      sceneRef.current = null
    }
  }, [layout])

  // Lock page scroll while the mall overlay is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  // Auto-hide the control hint after a few seconds
  useEffect(() => {
    const t = setTimeout(() => setHintVisible(false), 4500)
    return () => clearTimeout(t)
  }, [])

  const handleSelectFloor = (floorId: string) => {
    sceneRef.current?.teleportToFloor(floorId)
    setCurrentFloorId(floorId)
    setNearbyShop(null)
    setDismissedShopId(null)
  }

  const openFloorModal = () => {
    releasePointer() // the cursor is captured; give it back so the list is clickable
    setFloorModalOpen(true)
  }

  const currentFloor = layout.floors.find((f) => f.id === currentFloorId) ?? null

  return (
    <div className="mall3d-overlay" data-ambience={ambience}>
      <div className="mall3d-canvas-mount" ref={containerRef} />

      {controlMode === 'joystick' && layout.floors.length > 0 && (
        <MobileJoystick sceneRef={sceneRef} />
      )}

      {layout.floors.length === 0 && (
        <div className="mall3d-empty">No public shops to display yet.</div>
      )}

      <div className="mall3d-hud">
        <button className="mall3d-hud-btn mall3d-hud-btn--icon mall3d-exit-btn" onClick={onExit} title="Exit" aria-label="Exit">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
            <path d="M8 17l-5-5 5-5" />
            <path d="M3 12h12" />
          </svg>
        </button>

        {currentFloor && (
          <div className="mall3d-floor-indicator" style={{ ['--floor-rgb' as string]: currentFloor.rgb }}>
            <span className="mall3d-floor-dot" style={{ background: currentFloor.color }} />
            <span className="mall3d-floor-label">{currentFloor.category}</span>
          </div>
        )}

        <div className="mall3d-hud-right">
          <button
            className="mall3d-hud-btn mall3d-hud-btn--icon mall3d-controlmode-btn"
            onClick={toggleControlMode}
            title={`Movement: ${controlMode === 'joystick' ? 'Joystick' : 'Pinch gesture'}. Switch to ${controlMode === 'joystick' ? 'pinch gesture' : 'joystick'}.`}
            aria-label={`Movement: ${controlMode === 'joystick' ? 'Joystick' : 'Pinch gesture'}`}
          >
            {controlMode === 'joystick' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="8" />
                <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 8 3 3m0 0v4m0-4h4M16 8l5-5m0 0v4m0-4h-4M8 16l-5 5m0 0v-4m0 4h4M16 16l5 5m0 0v-4m0 4h-4" />
              </svg>
            )}
          </button>

          <button
            className="mall3d-hud-btn mall3d-hud-btn--icon mall3d-ambience-btn"
            onClick={toggleAmbience}
            title={`Lighting: ${AMBIENCE[ambience].label}. Switch to ${AMBIENCE[ambience === 'warm' ? 'daylight' : 'warm'].label}.`}
            aria-label={`Lighting: ${AMBIENCE[ambience].label}`}
          >
            {ambience === 'warm' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.2v.3h6v-.3c0-.8.4-1.6 1-2.2A6 6 0 0 0 12 3z" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
              </svg>
            )}
          </button>

          {layout.floors.length > 1 && (
            <button className="mall3d-hud-btn mall3d-floors-btn" onClick={openFloorModal}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2 3 7l9 5 9-5-9-5zm0 7.7L5.5 6 12 2.3 18.5 6 12 9.7zM3 12l9 5 9-5v2.2l-9 5-9-5V12zm0 4.3 9 5 9-5v2.2l-9 5-9-5v-2.2z" />
              </svg>
              Directory
            </button>
          )}
        </div>
      </div>

      {hintVisible && !activeShop && layout.floors.length > 0 && (
        <div className="mall3d-hint">
          <span className="mall3d-hint-desktop">
            {pointerLocked
              ? 'WASD to walk · move the mouse to look · Esc for the cursor'
              : 'Click to look around · WASD to walk'}
          </span>
          <span className="mall3d-hint-mobile">
            {controlMode === 'joystick' ? 'Drag to look · joystick to move' : '1 finger to look · pinch to move'}
          </span>
        </div>
      )}

      {floorModalOpen && (
        <FloorSelectModal
          floors={layout.floors}
          currentFloorId={currentFloorId}
          onSelect={handleSelectFloor}
          onClose={() => setFloorModalOpen(false)}
        />
      )}

      {activeShop && (
        <>
          <ScanPanel
            shop={activeShop}
            variant="mall"
            onEnter={() => onShopClick(activeShop)}
            onShare={(e) => onShare(e, activeShop)}
            onClose={() => setDismissedShopId(activeShop.id)}
          />
          {pointerLocked && (
            <div className="mall3d-interact-hint">
              Press <kbd>E</kbd> or click to enter · <kbd>Esc</kbd> for the cursor
            </div>
          )}
        </>
      )}

      {/* Closed by mistake while still standing in front of the shop — a tap
          (or E / click, same handler as entering) brings the card straight
          back instead of making you walk away and back to re-trigger it. */}
      {nearbyShop && !activeShop && (
        <button className="mall3d-reopen-btn" onClick={handleInteract}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 15 12 9l-6 6" />
          </svg>
          {nearbyShop.name}
        </button>
      )}
    </div>
  )
}
